/**
 * SkillsPage - Skills Marketplace
 * Hook'lar: useSkillsList | Component'lar: SkillCard, SkillFilters, SkillHero
 */
import { AlertCircle, Flame, Sparkles, Zap } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SkillCard from './components/SkillCard'
import SkillFilters from './components/SkillFilters'
import SkillHero from './components/SkillHero'
import SkillRunModal from './components/SkillRunModal'
import type { Skill } from './hooks/useSkills'
import { useSkillsList } from './hooks/useSkills'

export default function SkillsPage() {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState('All')
  const [sort, setSort] = useState('popular')
  const [installing, setInstalling] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [runningSkill, setRunningSkill] = useState<Skill | null>(null)
  const navigate = useNavigate()

  const { skills, loading, install } = useSkillsList(activeCategory, sort)

  const filtered = skills.filter(s =>
    !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.description.toLowerCase().includes(search.toLowerCase())
  )

  const featured = filtered.filter(s => s.featured)
  const rest = filtered.filter(s => !s.featured)

  const handleInstall = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setInstalling(id)
    setError(null)
    const ok = await install(id)
    if (!ok) {setError('Install failed. Please try again.')}
    setInstalling(null)
  }

  const handleRun = (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    const skill = skills.find(s => s.id === id)
    if (skill) { setRunningSkill(skill) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={20} color="#f59e0b" /> Skills Marketplace
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          Extend your agents with powerful skills. {skills.length} skills available.
        </p>
      </div>

      <SkillFilters
        search={search} onSearch={setSearch}
        activeCategory={activeCategory} onCategory={setActiveCategory}
        sort={sort} onSort={setSort}
      />

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '40px', textAlign: 'center' }}>
          Loading skills...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '12px' }}>
          <Zap size={32} color="#3b82f6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            {search ? 'No skills match your search.' : 'No skills in this category yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* Featured hero (ilk featured skill) */}
          {featured.length > 0 && !search && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <Flame size={15} color="#f59e0b" />
                <span style={{ fontSize: '13px', fontWeight: 700 }}>Featured</span>
              </div>
              <SkillHero
                skill={featured[0]}
                installing={installing}
                onInstall={(id, e) => { void handleInstall(id, e) }}
                onClick={() => navigate(`/aicloud/app/skills/${featured[0].id}`)}
              />
              {/* Diger featured'lar kart olarak */}
              {featured.length > 1 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px', marginTop: '12px' }}>
                  {featured.slice(1).map(skill => (
                    <SkillCard
                      key={skill.id} skill={skill} installing={installing}
                      onInstall={(id, e) => { void handleInstall(id, e) }}
                      onRun={handleRun}
                      featured
                      onClick={() => navigate(`/aicloud/app/skills/${skill.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* All skills */}
          {rest.length > 0 && (
            <div>
              {featured.length > 0 && !search && (
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '12px' }}>All Skills</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                {rest.map(skill => (
                  <SkillCard
                    key={skill.id} skill={skill} installing={installing}
                    onInstall={(id, e) => { void handleInstall(id, e) }}
                    onRun={handleRun}
                    onClick={() => navigate(`/aicloud/app/skills/${skill.id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Run Modal */}
      {runningSkill && (
        <SkillRunModal skill={runningSkill} onClose={() => setRunningSkill(null)} />
      )}
    </div>
  )
}
